/**
 * Ambiances et génération des jetons de thème (clair/sombre), portées du mockup (theme.jsx).
 *
 * Les couleurs sont définies en oklch puis converties en `rgb()` pour un rendu identique partout. Les
 * jetons produits sont appliqués comme variables CSS (`--bg`, `--accent`, …) sur le conteneur racine.
 */

export type Mode = "light" | "dark" | "system";
export type EffectiveMode = "light" | "dark";

export interface Ambiance {
  label: string;
  note: string;
  accentL: number;
  accentC: number;
  accentH: number;
  tintH: number;
  tintC: number;
  prefersDark: boolean;
  swatch: string;
}

export const AMBIANCES: Record<string, Ambiance> = {
  bleu: {
    label: "Bleu",
    note: "Le défaut — clair, calme, confiant",
    accentL: 0.54, accentC: 0.17, accentH: 264,
    tintH: 264, tintC: 0.006,
    prefersDark: false,
    swatch: "#3E63DD",
  },
  epure: {
    label: "Épuré",
    note: "Minimal — encre sur blanc, zéro distraction",
    accentL: 0.26, accentC: 0.012, accentH: 264,
    tintH: 264, tintC: 0.0,
    prefersDark: false,
    swatch: "#262629",
  },
  chaleureux: {
    label: "Chaleureux",
    note: "Doux — crème & terracotta, rassurant",
    accentL: 0.6, accentC: 0.125, accentH: 45,
    tintH: 72, tintC: 0.013,
    prefersDark: false,
    swatch: "#C2683C",
  },
  sombre: {
    label: "Sombre",
    note: "Focus — fond foncé, lueur indigo",
    accentL: 0.62, accentC: 0.15, accentH: 280,
    tintH: 280, tintC: 0.008,
    prefersDark: true,
    swatch: "#7C5CFF",
  },
  vif: {
    label: "Vif",
    note: "Énergique — corail saturé, vivant",
    accentL: 0.61, accentC: 0.21, accentH: 25,
    tintH: 25, tintC: 0.01,
    prefersDark: false,
    swatch: "#F0502E",
  },
};

export const AMBIANCE_LIST = ["bleu", "epure", "chaleureux", "sombre", "vif"];

/** oklch → chaîne `rgb()` (rendu identique quel que soit le moteur). */
function oklch(L: number, C: number, H: number): string {
  const hr = (H * Math.PI) / 180;
  const a = C * Math.cos(hr);
  const b = C * Math.sin(hr);
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;
  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const bl = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;
  const enc = (x: number): number => {
    x = Math.max(0, Math.min(1, x));
    x = x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055;
    return Math.round(x * 255);
  };
  return `rgb(${enc(r)}, ${enc(g)}, ${enc(bl)})`;
}

export type ThemeTokens = Record<string, string>;

export function makeTokens(ambKey: string, mode: EffectiveMode): ThemeTokens {
  const a = AMBIANCES[ambKey] ?? AMBIANCES.bleu!;
  const tH = a.tintH;
  const tC = a.tintC;

  if (mode === "dark") {
    const aL = Math.min(a.accentL + 0.06, 0.72);
    const aC = a.accentC;
    return {
      bg: oklch(0.155, tC * 0.7, tH),
      bgElev: oklch(0.185, tC * 0.7, tH),
      surface: oklch(0.215, tC * 0.6, tH),
      surface2: oklch(0.265, tC * 0.6, tH),
      text: oklch(0.955, 0.008, tH),
      subtext: oklch(0.68, 0.012, tH),
      faint: oklch(0.52, 0.01, tH),
      border: oklch(0.32, tC * 0.8, tH),
      borderStrong: oklch(0.4, tC * 0.8, tH),
      accent: oklch(aL, aC, a.accentH),
      accentSoft: oklch(0.3, Math.min(aC, 0.08), a.accentH),
      accentGlow: oklch(aL, aC, a.accentH),
      onAccent: oklch(0.99, 0.005, a.accentH),
      rec: oklch(0.62, 0.2, 22),
      recSoft: oklch(0.3, 0.1, 22),
    };
  }

  const isInk = ambKey === "epure";
  return {
    bg: oklch(0.975, tC, tH),
    bgElev: oklch(0.99, tC * 0.6, tH),
    surface: oklch(0.998, tC * 0.4, tH),
    surface2: oklch(0.955, tC, tH),
    text: oklch(0.22, 0.018, tH),
    subtext: oklch(0.5, 0.012, tH),
    faint: oklch(0.66, 0.01, tH),
    border: oklch(0.9, tC * 1.1, tH),
    borderStrong: oklch(0.84, tC * 1.1, tH),
    accent: oklch(a.accentL, a.accentC, a.accentH),
    accentSoft: oklch(0.95, Math.min(a.accentC, 0.05), a.accentH),
    accentGlow: oklch(a.accentL, a.accentC, a.accentH),
    onAccent: isInk ? oklch(0.99, 0, 0) : oklch(0.99, 0.005, a.accentH),
    rec: oklch(0.57, 0.21, 25),
    recSoft: oklch(0.94, 0.05, 25),
  };
}
